import { SvgIcon } from '@mui/material';
import React from 'react';
import { DeviceContext } from '../../../contexts/device/device';
import { theme } from '../../../style/global-style';
import { numberFormat, replaceVal } from '../../../utils/formatting';
import { __ } from '../../../utils/translation-service';
import { DreamlinesButton } from '../../atoms/dreamlines-button/dreamlines-button';
import { StyledRightArrowIcon } from '../../atoms/dreamlines-button/dreamlines-button.style';
import { ILabelBoxProps, LabelBox } from '../../atoms/label-box/label-box';
import { ILabelIconProps, LabelIcon } from '../../atoms/label-icon/label-icon';
import { Rating } from '../../atoms/rating/rating';
import { Sticky } from '../../hoc/sticky/sticky';
import {
    StyledTextLabelList,
    StyledTextLabelListItem,
    StyledUserLove,
} from '../information-board-card/information-board-card.style';
import { ITab, NavTabs } from '../nav-tabs/nav-tabs';
import { Price } from '../price';
import {
    Row1,
    Row2,
    StyledBoxButton,
    StyledCard,
    StyledHeadline,
    StyledNavTabs,
    StyledPriceAndButton,
    StyledSticky,
} from './floating-sticky-bar.style';

export interface IFloatingStickyBarProps {
    title: string;
    icons: ILabelIconProps[];
    labelItems: ILabelBoxProps[];
    constellationLabels: ILabelBoxProps[];
    ratingValue: number;
    ratingString: string;
    ratingAmount: number;
    price?: number;
    discount?: number;
    totalCount?: number;
    navItems: ITab[];
    onTabSelect: (index: number) => void;
    currentTabIndex?: number;
    stageModuleType: string | null;
    onSearchSubmit: () => void;
    observerRef: React.RefObject<HTMLElement | null>;
}

export const FloatingStickyBar: React.FC<IFloatingStickyBarProps> = (props) => {
    const device = React.useContext(DeviceContext);
    const isMobile = device === 'mobile';

    const formattedOfferCount =
        props?.totalCount && replaceVal(__('seeAllOffers'), numberFormat(props.totalCount));

    const constellationLabelList = props.constellationLabels.map((textLabel) => (
        <StyledTextLabelListItem key={textLabel.type}>
            <LabelBox type={textLabel.type} label={__(textLabel.type)} />
        </StyledTextLabelListItem>
    ));

    const labelList =
        props.labelItems &&
        props.labelItems.map((textLabel) => (
            <StyledTextLabelListItem key={textLabel.type}>
                <LabelBox type={textLabel.type} label={__(textLabel.type)} />
            </StyledTextLabelListItem>
        ));

    return (
        <Sticky observerRef={props.observerRef} height={isMobile ? 0 : undefined}>
            {(sticky) => (
                <StyledSticky
                    sticky={sticky}
                    stageModuleType={props.stageModuleType}
                    hide={isMobile}
                >
                    <StyledCard
                        data-testid="floating-sticky-bar"
                        sticky={sticky}
                        noTabs={props.navItems.length === 0}
                    >
                        <Row1>
                            <StyledHeadline
                                data-testid="headline"
                                style={{ flex: device === 'tablet' ? 'none' : 'unset' }}
                            >
                                {props.title}
                            </StyledHeadline>
                            {!isMobile ? (
                                <Rating
                                    value={props.ratingValue}
                                    string={props.ratingString}
                                    amount={props.ratingAmount}
                                    size="M"
                                />
                            ) : null}

                            <StyledTextLabelList
                                data-testid="constellation-labels"
                                style={{
                                    marginLeft: 20,
                                    marginTop: 0,
                                    display: sticky ? 'none' : 'initial',
                                }}
                            >
                                {constellationLabelList}
                            </StyledTextLabelList>
                        </Row1>
                        {props.stageModuleType === 'searchStage' && device === 'desktop' && (
                            <StyledTextLabelList
                                data-testid="label-items"
                                style={{ display: sticky || 'none' }}
                                rows={1}
                            >
                                {labelList}
                            </StyledTextLabelList>
                        )}
                        <Row2 style={{ alignSelf: 'stretch' }}>
                            {!!props?.totalCount && props.totalCount > 0 && (
                                <StyledPriceAndButton data-testid="price-and-btn" inRow={sticky}>
                                    <Price
                                        price={props.price}
                                        discount={props.discount}
                                        size={sticky ? (isMobile ? 'S' : 'M') : 'L'}
                                    />
                                    {!sticky ? (
                                        <DreamlinesButton onClick={props.onSearchSubmit}>
                                            {formattedOfferCount}
                                        </DreamlinesButton>
                                    ) : !isMobile ? (
                                        <StyledBoxButton
                                            wrapperStyle={{
                                                alignSelf: 'stretch',
                                            }}
                                            style={{
                                                marginLeft: 10,
                                                borderRadius: 0,
                                                height: '100%',
                                            }}
                                            onClick={props.onSearchSubmit}
                                        >
                                            {formattedOfferCount}
                                        </StyledBoxButton>
                                    ) : (
                                        <SvgIcon
                                            onClick={props.onSearchSubmit}
                                            style={{
                                                color: theme.colors.main4,
                                                marginLeft: 5,
                                                height: '100%',
                                            }}
                                        >
                                            <StyledRightArrowIcon />
                                        </SvgIcon>
                                    )}
                                </StyledPriceAndButton>
                            )}
                        </Row2>
                    </StyledCard>
                    {props?.navItems.length > 0 && (
                        <StyledNavTabs>
                            <NavTabs
                                title={props.title}
                                currentTabIndex={props.currentTabIndex}
                                tabs={props.navItems}
                                onSelect={props.onTabSelect}
                            />
                        </StyledNavTabs>
                    )}
                </StyledSticky>
            )}
        </Sticky>
    );
};
